import { X, Trash, Clock, PencilSimple, Check } from "@phosphor-icons/react";
import { useState } from "react";
import { STATUS_LABEL } from "../lib/labels";
import { api, formatApiError } from "../lib/api";

const STATUS_LABELS = {
  todo: STATUS_LABEL.todo,
  doing: STATUS_LABEL.doing,
  with_editor: STATUS_LABEL.with_editor,
  to_be_checked: STATUS_LABEL.to_be_checked,
  checked: STATUS_LABEL.checked,
  done: STATUS_LABEL.done,
};

const STATUS_COLORS = {
  todo: "#71717A",
  doing: "#007AFF",
  with_editor: "#A855F7",
  to_be_checked: "#F59E0B",
  checked: "#10B981",
  done: "#22C55E",
};

export function TaskDetailModal({ task, members, currentUser, onClose, onStatusChange, onDelete, canDelete, onTaskUpdate, onGfxGiven, onClaimChecker, onClaimEditor, onStartVideoEdit, onVideoEditDone }) {
  const memberMap = Object.fromEntries(members.map((m) => [m.id, m]));
  const worker = task.worker_id ? memberMap[task.worker_id] : null;
  const checker = task.checker_id ? memberMap[task.checker_id] : null;
  const videoEditor = task.video_editor_id ? memberMap[task.video_editor_id] : null;
  const graphicDesigner = task.graphic_designer_id ? memberMap[task.graphic_designer_id] : null;
  const publisher = task.publisher_id ? memberMap[task.publisher_id] : null;
  const assigner = task.assigner_id ? memberMap[task.assigner_id] : null;

  const role = currentUser.role;
  const isPublished = task.status === "done";
  const isAdmin = role === "admin";
  // Edit access is scoped to the specific people on THIS task —
  // the producer, the assigner who created it, and its copy editor.
  const isTaskAssigner = task.assigner_id === currentUser.id;
  const isTaskProducer = task.worker_id === currentUser.id;
  const isTaskChecker = task.checker_id === currentUser.id;
  // Any of the three (plus admin) can edit the title and script.
  // Admin can even edit after publish; the others cannot.
  const canEditDescription = isAdmin || ((isTaskAssigner || isTaskProducer || isTaskChecker) && !isPublished);
  const canEditTitle = canEditDescription;

  const [editingDesc, setEditingDesc] = useState(false);
  const [descDraft, setDescDraft] = useState(task.description || "");
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(task.title || "");
  const [saving, setSaving] = useState(false);

  const saveDescription = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/tasks/${task.id}`, { description: descDraft });
      onTaskUpdate?.(data);
      setEditingDesc(false);
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const saveTitle = async () => {
    const trimmed = titleDraft.trim();
    if (!trimmed) {
      alert("Title cannot be empty.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.patch(`/tasks/${task.id}`, { title: trimmed });
      onTaskUpdate?.(data);
      setEditingTitle(false);
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };
  const nextSet = new Set();
  const uid = currentUser.id;

  if (role === "admin") {
    Object.keys(STATUS_LABELS)
      .filter((s) => s !== task.status && s !== "with_editor")
      .forEach((s) => nextSet.add(s));
  }
  // Ready-to-Publish: task's assigner / producer / copy editor moves with_editor → checked
  if (task.status === "with_editor" && (isTaskAssigner || isTaskProducer || isTaskChecker)) {
    nextSet.add("checked");
  }
  // Producer transitions (worker + hybrid assigner)
  if ((role === "worker" || role === "assigner") && (!task.worker_id || task.worker_id === uid)) {
    const map = { todo: ["doing"], doing: ["to_be_checked"], checked: ["done"] };
    (map[task.status] || []).forEach((s) => nextSet.add(s));
  }
  // Video editor has NO status transitions in the new workflow
  // Copy editor transitions (checker + hybrid assigner)
  if ((role === "checker" || role === "assigner") && (!task.checker_id || task.checker_id === uid)) {
    if (task.status === "to_be_checked") {
      nextSet.add("with_editor");
      nextSet.add("doing");
    }
  }
  // Publisher
  if (role === "publisher" && (!task.publisher_id || task.publisher_id === uid)) {
    if (task.status === "checked") nextSet.add("done");
  }
  const nextOptions = Array.from(nextSet);
  const canMarkGfx = (role === "graphic_designer" || role === "admin")
    && task.status === "with_editor"
    && !task.gfx_given_at;
  // Copy editors / assigners can pre-claim a task before it hits their column.
  // Only shown while nobody has claimed it yet — matches the Kanban rule and
  // the backend guard (which returns 409 if another checker is already on it).
  const canPreClaimChecker = (role === "checker" || role === "assigner" || role === "admin")
    && (task.status === "doing" || task.status === "with_editor")
    && !task.checker_id;
  // Video editor self-claim (ownership only, no timer). Only useful once
  // someone else has claimed the task; unclaimed tasks are picked up by
  // clicking "I've started".
  const canClaimEditor = (role === "video_editor" || role === "admin")
    && task.status === "with_editor"
    && !!task.video_editor_id
    && task.video_editor_id !== currentUser.id;
  // "I've started" — records video_edit_started_at + self-claims if unset.
  const canStartVideoEdit = (role === "video_editor" || role === "admin")
    && task.status === "with_editor"
    && !task.video_edit_started_at
    && (!task.video_editor_id || task.video_editor_id === currentUser.id);
  // Video Edit Done marker — VE signals completion without moving status
  const canMarkVideoEditDone = (role === "video_editor" || role === "admin")
    && task.status === "with_editor"
    && !task.video_edit_done_at
    && (!task.video_editor_id || task.video_editor_id === currentUser.id);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} data-testid="task-detail-modal">
        <div className="flex items-start justify-between p-6 border-b border-[color:var(--border)]">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-2 h-2 rounded-full" style={{ background: STATUS_COLORS[task.status] }} />
              <span className="label-mono" style={{ color: STATUS_COLORS[task.status] }}>{STATUS_LABELS[task.status]}</span>
              {canEditTitle && !editingTitle && (
                <button
                  data-testid="edit-title-btn"
                  onClick={() => { setTitleDraft(task.title || ""); setEditingTitle(true); }}
                  className="ml-auto mr-8 inline-flex items-center gap-1 text-xs font-heading font-bold uppercase tracking-wider text-[color:var(--text-tertiary)] hover:text-white transition"
                  title="Edit title"
                >
                  <PencilSimple size={12} weight="bold" /> Edit title
                </button>
              )}
            </div>
            {editingTitle ? (
              <div className="space-y-2">
                <input
                  data-testid="title-editor"
                  value={titleDraft}
                  onChange={(e) => setTitleDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveTitle(); } if (e.key === "Escape") { setEditingTitle(false); setTitleDraft(task.title || ""); } }}
                  className="input w-full font-heading text-xl font-bold"
                  autoFocus
                />
                <div className="flex gap-2">
                  <button
                    data-testid="save-title-btn"
                    onClick={saveTitle}
                    disabled={saving}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-[color:var(--accent)] text-white text-xs font-heading font-bold uppercase tracking-wider disabled:opacity-50"
                  >
                    <Check size={12} weight="bold" /> {saving ? "Saving…" : "Save"}
                  </button>
                  <button
                    data-testid="cancel-title-btn"
                    onClick={() => { setEditingTitle(false); setTitleDraft(task.title || ""); }}
                    disabled={saving}
                    className="px-3 py-1.5 rounded border border-[color:var(--border)] text-xs font-heading font-bold uppercase tracking-wider text-[color:var(--text-secondary)] hover:text-white transition"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <h2 className="font-heading text-xl font-bold tracking-tight" data-testid="task-detail-title">{task.title}</h2>
            )}
          </div>
          <button onClick={onClose} data-testid="close-task-detail" className="text-[color:var(--text-secondary)] hover:text-white"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-5">
          <div data-testid="task-brief-section">
            <div className="flex items-center justify-between mb-2">
              <p className="label-mono">Script</p>
              {canEditDescription && !editingDesc && (
                <button
                  data-testid="edit-description-btn"
                  onClick={() => { setDescDraft(task.description || ""); setEditingDesc(true); }}
                  className="inline-flex items-center gap-1 text-xs font-heading font-bold uppercase tracking-wider text-[color:var(--text-tertiary)] hover:text-white transition"
                  title="Edit script"
                >
                  <PencilSimple size={12} weight="bold" /> Edit
                </button>
              )}
            </div>
            {editingDesc ? (
              <div className="space-y-2">
                <textarea
                  data-testid="description-editor"
                  value={descDraft}
                  onChange={(e) => setDescDraft(e.target.value)}
                  rows={12}
                  className="input w-full font-mono text-sm"
                  style={{ minHeight: 280, resize: "vertical" }}
                  placeholder="Paste or write the full script here…"
                  autoFocus
                />
                <div className="flex gap-2">
                  <button
                    data-testid="save-description-btn"
                    onClick={saveDescription}
                    disabled={saving}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-[color:var(--accent)] text-white text-xs font-heading font-bold uppercase tracking-wider disabled:opacity-50"
                  >
                    <Check size={12} weight="bold" /> {saving ? "Saving…" : "Save"}
                  </button>
                  <button
                    data-testid="cancel-description-btn"
                    onClick={() => { setEditingDesc(false); setDescDraft(task.description || ""); }}
                    disabled={saving}
                    className="px-3 py-1.5 rounded border border-[color:var(--border)] text-xs font-heading font-bold uppercase tracking-wider text-[color:var(--text-secondary)] hover:text-white transition"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : task.description ? (
              <p className="text-sm text-[color:var(--text-secondary)] whitespace-pre-wrap" data-testid="task-description">{task.description}</p>
            ) : (
              <p className="text-xs text-[color:var(--text-tertiary)] italic" data-testid="task-description-empty">No script yet.</p>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
            <Info label="Producer" value={worker?.name || "—"} />
            <Info label="Video Editor" value={videoEditor?.name || "—"} />
            <Info label="Graphic Designer" value={graphicDesigner?.name || "—"} />
            <Info label="Copy Editor" value={checker?.name || "—"} />
            <Info label="Publisher" value={publisher?.name || "—"} />
            <Info label="Assigner" value={assigner?.name || "—"} />
            <Info label="Due" value={task.due_date ? new Date(task.due_date).toLocaleDateString() : "—"} />
            <Info label="Created" value={new Date(task.created_at).toLocaleDateString()} />
            <Info label="GFX Given" value={task.gfx_given_at ? new Date(task.gfx_given_at).toLocaleString() : "—"} />
            <Info label="Video Edit Started" value={task.video_edit_started_at ? new Date(task.video_edit_started_at).toLocaleString() : "—"} />
            <Info label="Video Edit Done" value={task.video_edit_done_at ? new Date(task.video_edit_done_at).toLocaleString() : "—"} />
          </div>

          {(nextOptions.length > 0 || canMarkGfx || canPreClaimChecker || canClaimEditor || canStartVideoEdit || canMarkVideoEditDone) && (
            <div>
              <p className="label-mono mb-2">Actions</p>
              <div className="flex flex-wrap gap-2">
                {canMarkVideoEditDone && (
                  <button
                    data-testid="detail-video-edit-done"
                    onClick={() => onVideoEditDone?.()}
                    className="text-xs font-heading font-black uppercase tracking-wider px-3 py-1.5 rounded border transition"
                    style={{ color: "#C084FC", borderColor: "#C084FC", background: "rgba(192, 132, 252, 0.10)" }}
                  >
                    Video Edit Done
                  </button>
                )}
                {canStartVideoEdit && (
                  <button
                    data-testid="detail-start-video-edit"
                    onClick={() => onStartVideoEdit?.()}
                    className="text-xs font-heading font-black uppercase tracking-wider px-3 py-1.5 rounded border transition"
                    style={{ color: "#C084FC", borderColor: "#C084FC", background: "rgba(192, 132, 252, 0.10)" }}
                    title="Start the editor timer + claim this task"
                  >
                    I've started
                  </button>
                )}
                {canClaimEditor && (
                  <button
                    data-testid="detail-claim-editor"
                    onClick={() => onClaimEditor?.()}
                    className="text-xs font-heading font-bold uppercase tracking-wider px-3 py-1.5 rounded border transition"
                    style={{ color: "#C084FC", borderColor: "#C084FC55" }}
                    title="Take over as the video editor for this task"
                  >
                    Take over as Video Editor
                  </button>
                )}
                {canMarkGfx && (
                  <button
                    data-testid="detail-mark-gfx-given"
                    onClick={() => onGfxGiven?.()}
                    className="text-xs font-heading font-bold uppercase tracking-wider px-3 py-1.5 rounded border transition"
                    style={{ color: "#38BDF8", borderColor: "#38BDF855" }}
                  >
                    GFX Given
                  </button>
                )}
                {canPreClaimChecker && (
                  <button
                    data-testid="detail-claim-checker"
                    onClick={() => onClaimChecker?.()}
                    className="text-xs font-heading font-bold uppercase tracking-wider px-3 py-1.5 rounded border transition"
                    style={{ color: "#FBBF24", borderColor: "#FBBF2455" }}
                    title="Claim this task as its Copy Editor before it lands in your column"
                  >
                    Claim as Copy Editor
                  </button>
                )}
                {nextOptions.map((s) => (
                  <button
                    key={s}
                    data-testid={`detail-move-to-${s}`}
                    onClick={() => onStatusChange(task.id, s)}
                    className="text-xs font-heading font-bold uppercase tracking-wider px-3 py-1.5 rounded border transition"
                    style={{ color: STATUS_COLORS[s], borderColor: STATUS_COLORS[s] + "55" }}
                  >
                    {STATUS_LABELS[s]}
                  </button>
                ))}
              </div>
            </div>
          )}

          {task.status_history?.length > 0 && (
            <div>
              <p className="label-mono mb-2 flex items-center gap-2"><Clock size={12} /> Timeline</p>
              <div className="space-y-2">
                {task.status_history.slice(-6).map((h) => (
                  <div key={`${h.at}-${h.status}`} className="flex items-center gap-3 text-xs">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: STATUS_COLORS[h.status] }} />
                    <span className="font-mono text-[color:var(--text-secondary)]" style={{ color: STATUS_COLORS[h.status] }}>{STATUS_LABELS[h.status]}</span>
                    <span className="font-mono text-[color:var(--text-tertiary)]">{new Date(h.at).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {canDelete && (
            <div className="pt-4 border-t border-[color:var(--border)]">
              <button data-testid="delete-task-button" onClick={onDelete} className="btn-danger">
                <Trash size={14} /> Delete task
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <p className="label-mono mb-1">{label}</p>
      <p className="text-white text-sm font-semibold">{value}</p>
    </div>
  );
}
